import { FieldHistoryPanel } from './field-history-panel';
import {
    Badge,
    Box,
    Button,
    Group,
    Select,
    SimpleGrid,
    Stack,
    Switch,
    TagsInput,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { type UseFormReturnType, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { CityEstablishmentRO } from '@routellers/types/dist/city-establishment/city-establishment.ro';
import { EstablishmentKindEnum } from '@routellers/types/dist/city-establishment/enum/establishment-kind.enum';
import { EstablishmentLocaleStatusEnum } from '@routellers/types/dist/city-establishment/enum/establishment-locale-status.enum';
import {
    ESTABLISHMENT_OFFER_ORDER,
    EstablishmentOfferEnum,
} from '@routellers/types/dist/city-establishment/enum/establishment-offer.enum';
import {
    ESTABLISHMENT_STATUS_ORDER,
    EstablishmentStatusEnum,
} from '@routellers/types/dist/city-establishment/enum/establishment-status.enum';
import {
    ESTABLISHMENT_WEEKDAYS,
    EstablishmentHours,
    EstablishmentWeekday,
} from '@routellers/types/dist/city-establishment/establishment-hours';
import type { EstablishmentIntakeProfile } from '@routellers/types/dist/city-establishment/establishment-intake';
import {
    ESTABLISHMENT_SPECIALTIES,
    specialtyLabel,
} from '@routellers/types/dist/city-establishment/establishment-specialties';
import { CONTENT_BASE_LANGUAGE } from '@routellers/types/dist/shared/content-base-language';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    IconBrandInstagram,
    IconBrandLine,
    IconBrandWhatsapp,
    IconCopy,
} from '@tabler/icons-react';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { DistrictSelect } from '../components/district-select';
import {
    EditorFooter,
    EntityEditorShell,
    LanguageChips,
    PhotoUpload,
    Section,
} from '../components/editor';
import {
    emptyHours,
    type HoursDayForm,
    hoursFrom,
    hoursPayload,
    unpairedHourDay,
} from '../components/week-hours-fields';
import { enumLabel, enumOptions } from '../i18n';
import { contentLanguagesForCountry } from '../utils/content-languages';

import { CitySearchSelect } from './city-search-select';
import { EstablishmentFactsPanel } from './establishment-facts-panel';
import {
    EstablishmentIntakeTab,
    type IntakeShape,
    intakeAnswers,
} from './establishment-intake-tab';

type Props = {
    opened: boolean;
    zIndex?: number;
    onClose: () => void;
    establishmentId: number | null;
    defaultCityId?: number | null;
    onSaved: () => void;
    onCreated?: (id: number) => void;
    onDeleted?: () => void;
};

type CardForm = {
    title: string;
    slug: string;
    cityId: number | null;
    districtId: number | null;
    kind: EstablishmentKindEnum;
    offers: EstablishmentOfferEnum[];
    address: string;
    neighborhood: string;
    lat: string;
    lng: string;
    instagram: string;
    whatsapp: string;
    facebook: string;
    line: string;
    website: string;
    phone: string;
    operator: string;
    openedYear: string;
    priceLevel: string | null;
    status: EstablishmentStatusEnum;
    closedNote: string;
    hours: Record<EstablishmentWeekday, HoursDayForm>;
    hoursNote: string;
    specialties: string[];
    cuisines: string[];
    drinks: string[];
    rooms: string[];
    pets: string;
    age: string;
    booking: string;
    cashOnly: string;
    photoCredit: string;
};

type LocaleDraft = {
    name: string;
    pitch: string;
    findHint: string;
    hoursNote: string;
    tags: string[];
    status: EstablishmentLocaleStatusEnum;
};

const statusColor: Record<EstablishmentStatusEnum, string> = {
    [EstablishmentStatusEnum.DRAFT]: 'gray',
    [EstablishmentStatusEnum.PUBLISHED]: 'green',
    [EstablishmentStatusEnum.CLOSED]: 'red',
};

const emptyCard = (cityId?: number | null): CardForm => ({
    title: '',
    slug: '',
    cityId: cityId ?? null,
    districtId: null,
    kind: EstablishmentKindEnum.SPOT,
    offers: [],
    address: '',
    neighborhood: '',
    lat: '',
    lng: '',
    instagram: '',
    whatsapp: '',
    facebook: '',
    line: '',
    website: '',
    phone: '',
    operator: '',
    openedYear: '',
    priceLevel: null,
    status: EstablishmentStatusEnum.DRAFT,
    closedNote: '',
    hours: emptyHours(),
    hoursNote: '',
    specialties: [],
    cuisines: [],
    drinks: [],
    rooms: [],
    pets: '',
    age: '',
    booking: '',
    cashOnly: '',
    photoCredit: '',
});

const emptyLocale = (): LocaleDraft => ({
    name: '',
    pitch: '',
    findHint: '',
    hoursNote: '',
    tags: [],
    status: EstablishmentLocaleStatusEnum.EMPTY,
});

const baseHoursNote = (row: CityEstablishmentRO) =>
    row.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE)?.hoursNote || '';

const cardFrom = (row: CityEstablishmentRO): CardForm => ({
    title: row.title || '',
    slug: row.slug || '',
    cityId: row.cityId,
    districtId: row.districtId ?? null,
    kind: row.kind,
    offers: row.offers || [],
    address: row.address || '',
    neighborhood: row.neighborhood || '',
    lat: row.lat == null ? '' : String(row.lat),
    lng: row.lng == null ? '' : String(row.lng),
    instagram: row.instagram || '',
    whatsapp: row.whatsapp || '',
    facebook: row.facebook || '',
    line: row.line || '',
    website: row.website || '',
    phone: row.phone || '',
    operator: row.operator || '',
    openedYear: row.openedYear == null ? '' : String(row.openedYear),
    priceLevel: row.priceLevel == null ? null : String(row.priceLevel),
    status: row.status,
    closedNote: row.closedNote || '',
    hours: hoursFrom(row.hours),
    hoursNote: baseHoursNote(row),
    specialties: row.specialties || [],
    cuisines: row.profile?.cuisines || [],
    drinks: row.profile?.drinks || [],
    rooms: row.profile?.rooms || [],
    pets: row.profile?.pets || '',
    age: row.profile?.age || '',
    booking: row.profile?.booking || '',
    cashOnly: row.profile?.cashOnly == null ? '' : row.profile.cashOnly ? 'yes' : 'no',
    photoCredit: row.profile?.photoCredit || '',
});

const draftsFrom = (row: CityEstablishmentRO): Record<string, LocaleDraft> => {
    const drafts: Record<string, LocaleDraft> = {};

    for (const language of contentLanguagesForCountry(row.country)) {
        const locale = (row.locales || []).find((item) => item.language === language);

        drafts[language] = locale
            ? {
                  name: locale.name || '',
                  pitch: locale.pitch || '',
                  findHint: locale.findHint || '',
                  hoursNote: locale.hoursNote || '',
                  tags: locale.tags || [],
                  status: locale.status,
              }
            : emptyLocale();
    }

    return drafts;
};

const parseCoord = (value: string): number | null => {
    const trimmed = value.trim();

    if (!trimmed) return null;
    const n = Number(trimmed);

    return Number.isFinite(n) ? n : Number.NaN;
};

const stamp = (value: string | null | undefined, locale: string) => {
    if (!value) return '—';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString(locale.startsWith('en') ? 'en-GB' : 'ru-RU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const whatsappDigits = (href: string) => {
    const fromLink = href.match(/wa\.me\/(\d+)/i)?.[1];
    const digits = (fromLink || href.replace(/\D/g, '')).replace(/^00/, '');

    return digits.length >= 8 && digits.length <= 15 ? digits : null;
};

const instagramHandle = (href: string) => {
    const handle = href
        .replace(/^@/, '')
        .replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, '')
        .split(/[/?#]/)[0];

    return handle && /^[\w.]{1,30}$/.test(handle) ? handle : null;
};

const lineAccount = (href: string) => {
    const raw = href
        .replace(/^https?:\/\/(?:www\.)?(?:line\.me|lin\.ee)\//i, '')
        .replace(/^(?:ti\/p|R\/ti\/p|R\/oaMessage)\//i, '')
        .split(/[/?#]/)[0]
        .replace(/^[@~]/, '');

    return raw && /^[\w.-]{2,30}$/.test(raw) ? raw : null;
};

const messageIsPrefilled = (kind: 'whatsapp' | 'line' | 'instagram', source: string) => {
    if (kind === 'instagram' || /lin\.ee\//i.test(source)) return false;
    if (kind === 'whatsapp') return Boolean(whatsappDigits(source));

    return Boolean(lineAccount(source));
};

const sendHref = (kind: 'whatsapp' | 'line' | 'instagram', source: string, message: string) => {
    if (kind === 'whatsapp') {
        const digits = whatsappDigits(source);

        if (digits) return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
        if (/wa\.me\/message\//i.test(source)) return source;

        return null;
    }

    if (kind === 'instagram') {
        const handle = instagramHandle(source);

        return handle ? `https://ig.me/m/${handle}` : null;
    }

    if (/lin\.ee\//i.test(source)) return source;

    const account = lineAccount(source);

    return account
        ? `https://line.me/R/oaMessage/@${account}/?${encodeURIComponent(message)}`
        : null;
};

const hoursLine = (
    hours: EstablishmentHours | null,
    label: (day: EstablishmentWeekday) => string,
) => {
    if (!hours) return '';

    return ESTABLISHMENT_WEEKDAYS.map((day) => {
        const row = hours[day];

        if (!row) return null;
        if (row.closed) return `${label(day)} ×`;
        if (row.open && row.close) return `${label(day)} ${row.open}–${row.close}`;

        return null;
    })
        .filter(Boolean)
        .join(', ');
};

const blank = (value: string | number | null | undefined) =>
    value == null ? '' : String(value).trim();

const pinText = (lat: number | null, lng: number | null) =>
    lat == null || lng == null ? '' : `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;

const offerText = (
    offers: EstablishmentOfferEnum[],
    label: (offer: EstablishmentOfferEnum) => string,
) =>
    ESTABLISHMENT_OFFER_ORDER.filter((offer) => offers.includes(offer))
        .map(label)
        .join(', ');

const profileHasAnswers = (profile: EstablishmentIntakeProfile) =>
    Boolean(
        profile.cuisines.length ||
            profile.drinks.length ||
            profile.rooms.length ||
            profile.pets ||
            profile.age ||
            profile.booking ||
            profile.cashOnly ||
            profile.photoCredit ||
            (profile.specialties || []).length,
    );

const changesOf = (
    row: CityEstablishmentRO,
    label: (key: string) => string,
    dayLabel: (day: EstablishmentWeekday) => string,
    offerLabel: (offer: EstablishmentOfferEnum) => string,
    language: string,
) => {
    const pack = row.intakePending;

    if (!pack) return [];

    const lines: { label: string; value: string }[] = [];
    const push = (key: string, live: string, next: string) => {
        if (live === next) return;
        lines.push({ label: label(key), value: next || '—' });
    };

    push('title', blank(row.title), blank(pack.title));
    push('address', blank(row.address), blank(pack.address));
    push('coordsLabel', pinText(row.lat, row.lng), pinText(pack.lat, pack.lng));
    push('hours', hoursLine(row.hours, dayLabel), hoursLine(pack.hours, dayLabel));
    push('phone', blank(row.phone), blank(pack.phone));
    push('website', blank(row.website), blank(pack.website));
    push('instagram', blank(row.instagram), blank(pack.instagram));
    push('whatsapp', blank(row.whatsapp), blank(pack.whatsapp));
    push('facebook', blank(row.facebook), blank(pack.facebook));
    push('line', blank(row.line), blank(pack.line));
    push('operator', blank(row.operator), blank(pack.operator));
    push('openedYear', blank(row.openedYear), blank(pack.openedYear));
    push(
        'priceLevel',
        row.priceLevel ? '$'.repeat(row.priceLevel) : '',
        pack.priceLevel ? '$'.repeat(pack.priceLevel) : '',
    );
    push(
        'offers',
        offerText(row.offers || [], offerLabel),
        offerText(pack.offers || [], offerLabel),
    );
    push(
        'hoursNote',
        blank(row.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE)?.hoursNote),
        blank(pack.hoursNote),
    );
    push('closedNote', blank(row.closedNote), blank(pack.closedNote));
    push(
        'specialties',
        (row.specialties || [])
            .map((code) => specialtyLabel(code, language) || '')
            .filter(Boolean)
            .join(', '),
        (pack.profile?.specialties || [])
            .map((code) => specialtyLabel(code, language) || '')
            .filter(Boolean)
            .join(', '),
    );

    if (JSON.stringify(row.photos || []) !== JSON.stringify(pack.photos || [])) {
        lines.push({ label: label('photos'), value: String(pack.photos.length) });
    }

    return lines;
};

export const EstablishmentEditor = ({
    opened,
    zIndex,
    onClose,
    establishmentId,
    defaultCityId,
    onSaved,
    onCreated,
    onDeleted,
}: Props) => {
    const { t, i18n } = useTranslation();
    const isCreate = establishmentId == null;
    const [tab, setTab] = useState('card');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [row, setRow] = useState<CityEstablishmentRO | null>(null);
    const [photos, setPhotos] = useState<string[]>([]);
    const [cover, setCover] = useState<number | null>(null);
    const [photosBaseline, setPhotosBaseline] = useState('');
    const [lang, setLang] = useState<Languages>(CONTENT_BASE_LANGUAGE);
    const [drafts, setDrafts] = useState<Record<string, LocaleDraft>>({});
    const [draftsBaseline, setDraftsBaseline] = useState('');
    const form = useForm<CardForm>({ initialValues: emptyCard() });

    const applyRow = (
        data: CityEstablishmentRO,
        keep?: { drafts?: boolean; photos?: boolean; card?: boolean },
    ) => {
        const nextDrafts = draftsFrom(data);

        setRow(data);
        if (!keep?.card) {
            form.setValues(cardFrom(data));
            form.resetDirty();
        }
        if (!keep?.photos) {
            setPhotos(data.photos || []);
            setCover(data.coverPhotoIndex ?? null);
            setPhotosBaseline(
                JSON.stringify({ photos: data.photos || [], cover: data.coverPhotoIndex ?? null }),
            );
        }
        if (!keep?.drafts) {
            setDrafts(nextDrafts);
            setDraftsBaseline(JSON.stringify(nextDrafts));
        }
    };

    useEffect(() => {
        if (!opened) return;

        setTab('card');
        setLang(CONTENT_BASE_LANGUAGE);
        setRow(null);
        setPhotos([]);
        setCover(null);
        setPhotosBaseline(JSON.stringify({ photos: [], cover: null }));
        setDrafts({});
        setDraftsBaseline('{}');

        if (isCreate) {
            form.setValues(emptyCard(defaultCityId));
            form.resetDirty();
            setLoading(false);

            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityEstablishmentsApi.getOne(establishmentId!);

                if (cancelled || !data) return;
                applyRow(data);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
                onClose();
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
        // form identity is stable enough; reload when the opened record changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, establishmentId]);

    const languages = contentLanguagesForCountry(row?.country);
    const draft = drafts[lang] || emptyLocale();
    const localeDirty = JSON.stringify(drafts) !== draftsBaseline;
    const photosDirty = JSON.stringify({ photos, cover }) !== photosBaseline;
    const formDirty = form.isDirty();
    const cardFields: (keyof CardForm)[] = isCreate
        ? ['title', 'slug', 'cityId', 'districtId', 'kind', 'status', 'neighborhood']
        : ['slug', 'cityId', 'districtId', 'kind', 'status', 'neighborhood'];
    const intakeFields = [
        'title',
        'offers',
        'address',
        'lat',
        'lng',
        'instagram',
        'whatsapp',
        'facebook',
        'line',
        'website',
        'phone',
        'operator',
        'openedYear',
        'priceLevel',
        'closedNote',
        'hours',
        'hoursNote',
        'specialties',
        'cuisines',
        'drinks',
        'rooms',
        'pets',
        'age',
        'booking',
        'cashOnly',
        'photoCredit',
    ] as const;
    const cardDirty = cardFields.some((key) => form.isDirty(key));
    const intakeDirty = !isCreate && intakeFields.some((key) => form.isDirty(key));
    const specialtyChoices = useMemo(() => {
        const language = i18n.language?.toLowerCase().startsWith('ru')
            ? Languages.RU
            : Languages.EN;

        return ESTABLISHMENT_SPECIALTIES.map((item) => ({
            value: item.code,
            label: item.names[language],
        }));
    }, [i18n.language]);

    const chips = useMemo(
        () =>
            languages.map((language) => {
                const item = drafts[language] || emptyLocale();
                const saved = draftsBaseline ? JSON.parse(draftsBaseline)[language] : null;
                let mark: 'filled' | 'reviewed' | 'dirty' | 'empty' = 'empty';

                if (JSON.stringify(item) !== JSON.stringify(saved || emptyLocale())) mark = 'dirty';
                else if (item.status === EstablishmentLocaleStatusEnum.READY) mark = 'reviewed';
                else if (
                    item.name.trim() ||
                    item.pitch.trim() ||
                    item.findHint.trim() ||
                    item.tags.length
                ) {
                    mark = 'filled';
                }

                return { value: language, label: language.toUpperCase(), mark };
            }),
        [languages, drafts, draftsBaseline],
    );

    const kindOptions = enumOptions(
        [EstablishmentKindEnum.SPOT, EstablishmentKindEnum.CLUSTER],
        t,
        'enums.establishmentKind',
    );
    const statusOptions = enumOptions(ESTABLISHMENT_STATUS_ORDER, t, 'enums.establishmentStatus');

    const patchDraft = (patch: Partial<LocaleDraft>) => {
        setDrafts((prev) => ({
            ...prev,
            [lang]: { ...(prev[lang] || emptyLocale()), ...patch },
        }));
    };

    const applyCardSave = (data: CityEstablishmentRO) => {
        const serverDrafts = draftsFrom(data);
        const keepDrafts = JSON.stringify(drafts) !== draftsBaseline;

        applyRow(data, { drafts: keepDrafts, photos: photosDirty });

        if (!keepDrafts) return;

        const serverBase = serverDrafts[CONTENT_BASE_LANGUAGE];

        if (!serverBase) return;

        const saved = JSON.parse(draftsBaseline || '{}') as Record<string, LocaleDraft>;
        const baselineBase = saved[CONTENT_BASE_LANGUAGE] || emptyLocale();
        const localBase = drafts[CONTENT_BASE_LANGUAGE] || emptyLocale();
        const tagsChanged =
            JSON.stringify(baselineBase.tags || []) !== JSON.stringify(serverBase.tags || []);
        const nextLocal: LocaleDraft = {
            ...localBase,
            hoursNote: serverBase.hoursNote,
            tags: tagsChanged ? serverBase.tags : localBase.tags,
        };
        const nextBaseline: LocaleDraft = {
            ...baselineBase,
            hoursNote: serverBase.hoursNote,
            tags: tagsChanged ? serverBase.tags : baselineBase.tags,
        };

        setDrafts((prev) => ({ ...prev, [CONTENT_BASE_LANGUAGE]: nextLocal }));
        setDraftsBaseline(JSON.stringify({ ...saved, [CONTENT_BASE_LANGUAGE]: nextBaseline }));
    };

    const saveCard = async () => {
        const title = form.values.title.trim();

        if (!title || !form.values.cityId) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.required'),
            });

            return;
        }

        const brokenDay = unpairedHourDay(form.values.hours);

        if (brokenDay) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.hoursPair', {
                    day: t(`enums.weekday.${brokenDay}`),
                }),
            });

            return;
        }

        const lat = parseCoord(form.values.lat);
        const lng = parseCoord(form.values.lng);

        if (Number.isNaN(lat) || Number.isNaN(lng) || (lat == null) !== (lng == null)) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.coords'),
            });

            return;
        }

        let openedYear: number | null = null;

        if (form.values.openedYear.trim()) {
            openedYear = Number(form.values.openedYear);
            if (!Number.isInteger(openedYear) || openedYear < 1800 || openedYear > 2100) {
                notifications.show({
                    color: 'red',
                    message: t('pages.establishments.editor.year'),
                });

                return;
            }
        }

        const readySaved = (row?.locales || []).some(
            (locale) => locale.status === EstablishmentLocaleStatusEnum.READY,
        );

        if (form.values.status === EstablishmentStatusEnum.PUBLISHED && !readySaved) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.publishNeedsText'),
            });

            return;
        }

        const payload = {
            title,
            slug: form.values.slug.trim(),
            cityId: form.values.cityId,
            districtId: form.values.districtId,
            kind: form.values.kind,
            offers: form.values.offers,
            hours: hoursPayload(form.values.hours),
            address: form.values.address.trim() || null,
            neighborhood: form.values.neighborhood.trim() || null,
            lat,
            lng,
            instagram: form.values.instagram.trim() || null,
            whatsapp: form.values.whatsapp.trim() || null,
            facebook: form.values.facebook.trim() || null,
            line: form.values.line.trim() || null,
            website: form.values.website.trim() || null,
            phone: form.values.phone.trim() || null,
            operator: form.values.operator.trim() || null,
            openedYear,
            priceLevel: form.values.priceLevel ? Number(form.values.priceLevel) : null,
            status: form.values.status,
            closedNote: form.values.closedNote.trim() || null,
            ...intakeAnswers(form.values),
        };

        setSaving(true);

        try {
            if (isCreate) {
                const { data } = await api.cityEstablishmentsApi.create({ data: payload });

                notifications.show({
                    color: 'green',
                    message: t('pages.establishments.editor.created'),
                });
                onSaved();
                if (data?.id) onCreated?.(data.id);
            } else {
                const { data } = await api.cityEstablishmentsApi.update({
                    id: establishmentId!,
                    data: payload,
                });
                let saved = data;

                if (saved) {
                    const note = form.values.hoursNote.trim();
                    const previous = baseHoursNote(row!).trim();

                    if (note !== previous) {
                        const localeRes = await api.cityEstablishmentsApi.upsertLocale({
                            id: establishmentId!,
                            data: {
                                language: CONTENT_BASE_LANGUAGE,
                                hoursNote: note || null,
                            },
                        });

                        if (localeRes.data) saved = localeRes.data;
                    }

                    applyCardSave(saved);
                }
                notifications.show({
                    color: 'green',
                    message: t('pages.establishments.editor.saved'),
                });
                onSaved();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const saveLocale = async () => {
        if (!establishmentId) return;

        const current = drafts[lang] || emptyLocale();

        if (
            current.status === EstablishmentLocaleStatusEnum.READY &&
            (!current.name.trim() || !current.pitch.trim())
        ) {
            notifications.show({
                color: 'red',
                message: t('pages.establishments.editor.readyNeedsCopy'),
            });

            return;
        }

        setSaving(true);

        try {
            const { data } = await api.cityEstablishmentsApi.upsertLocale({
                id: establishmentId,
                data: {
                    language: lang,
                    name: current.name.trim(),
                    pitch: current.pitch.trim() || null,
                    findHint: current.findHint.trim() || null,
                    tags: current.tags,
                    status: current.status,
                    ...(lang === CONTENT_BASE_LANGUAGE
                        ? {}
                        : { hoursNote: current.hoursNote.trim() || null }),
                },
            });

            if (data) {
                const nextDrafts = draftsFrom(data);

                setRow(data);
                setDrafts(nextDrafts);
                setDraftsBaseline(JSON.stringify(nextDrafts));
            }
            notifications.show({
                color: 'green',
                message: t('pages.establishments.editor.saved'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const savePhotos = async () => {
        if (!establishmentId) return;
        setSaving(true);

        try {
            const { data } = await api.cityEstablishmentsApi.update({
                id: establishmentId,
                data: {
                    photos,
                    coverPhotoIndex: photos.length ? (cover ?? 0) : null,
                },
            });

            if (data) applyRow(data, { drafts: localeDirty, card: formDirty });
            notifications.show({
                color: 'green',
                message: t('pages.establishments.editor.saved'),
            });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!establishmentId || !row) return;
        if (!window.confirm(t('pages.establishments.editor.confirmDelete', { title: row.title }))) {
            return;
        }

        setSaving(true);

        try {
            await api.cityEstablishmentsApi.remove(establishmentId);
            notifications.show({
                color: 'green',
                message: t('pages.establishments.editor.deleted'),
            });
            onDeleted?.();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const copyIntakeLink = async () => {
        if (!establishmentId) return;

        try {
            const { data } = await axios.get<{ url: string }>(
                `${process.env.REACT_APP_API_DOMAIN || ''}/admin/city-establishments/${establishmentId}/intake-link`,
                { withCredentials: true },
            );

            await navigator.clipboard.writeText(data.url);
            notifications.show({ message: t('pages.establishments.editor.intakeCopied') });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return;
        }

        try {
            const fresh = await api.cityEstablishmentsApi.getOne(establishmentId);

            if (fresh.data) applyRow(fresh.data, { card: true, photos: true, drafts: true });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const sendIntake = async (kind: 'whatsapp' | 'line' | 'instagram') => {
        if (!establishmentId || !row) return;

        const source =
            kind === 'whatsapp' ? row.whatsapp : kind === 'line' ? row.line : row.instagram;
        const popup = window.open('', '_blank');

        if (!source) {
            popup?.close();

            return;
        }

        try {
            const { data } = await axios.get<{ url: string; message: string }>(
                `${process.env.REACT_APP_API_DOMAIN || ''}/admin/city-establishments/${establishmentId}/intake-link`,
                { withCredentials: true },
            );
            const message = data.message || data.url;
            const href = sendHref(kind, source, message);

            if (!href) {
                popup?.close();
                notifications.show({
                    color: 'yellow',
                    message: t('pages.establishments.editor.intakeNoChannel'),
                });

                return;
            }

            await navigator.clipboard.writeText(message);
            if (popup) popup.location.href = href;
            else window.open(href, '_blank');
            notifications.show({
                message: t(
                    messageIsPrefilled(kind, source)
                        ? 'pages.establishments.editor.intakeChatOpened'
                        : 'pages.establishments.editor.intakeInstagramPaste',
                ),
            });
        } catch (error) {
            popup?.close();
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return;
        }

        try {
            const fresh = await api.cityEstablishmentsApi.getOne(establishmentId);

            if (fresh.data) applyRow(fresh.data, { card: true, photos: true, drafts: true });
            onSaved();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const reviewIntake = async (action: 'approve' | 'reject') => {
        if (!establishmentId) return;
        if (
            !window.confirm(
                t(
                    action === 'approve'
                        ? 'pages.establishments.editor.confirmApprove'
                        : 'pages.establishments.editor.confirmReject',
                ),
            )
        ) {
            return;
        }

        setSaving(true);

        try {
            const { data } = await axios.post<CityEstablishmentRO>(
                `${process.env.REACT_APP_API_DOMAIN || ''}/admin/city-establishments/${establishmentId}/intake-review`,
                { action },
                { withCredentials: true },
            );

            applyRow(
                data,
                action === 'reject' ? { card: true, photos: true, drafts: true } : undefined,
            );
            onSaved();
            notifications.show({
                color: 'green',
                message: t(
                    action === 'approve'
                        ? 'pages.establishments.editor.intakeApproved'
                        : 'pages.establishments.editor.intakeRejectedDone',
                ),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const mapLat = parseCoord(form.values.lat);
    const mapLng = parseCoord(form.values.lng);
    const mapHref =
        mapLat != null && mapLng != null && !Number.isNaN(mapLat) && !Number.isNaN(mapLng)
            ? `https://www.google.com/maps?q=${mapLat},${mapLng}`
            : null;
    const showPack = Boolean(
        row?.intakePending && (row.intakeReview === 'pending' || row.intakeReview === 'rejected'),
    );
    const packLines =
        showPack && row
            ? changesOf(
                  row,
                  (key) => t(`pages.establishments.editor.${key}`),
                  (day) => t(`enums.weekday.${day}`),
                  (offer) => t(`enums.establishmentOffer.${offer}`),
                  i18n.language,
              )
            : [];
    const hasChannel = Boolean(row?.whatsapp || row?.facebook || row?.instagram || row?.line);

    return (
        <EntityEditorShell
            zIndex={zIndex}
            opened={opened}
            onClose={onClose}
            loading={loading}
            size='xl'
            title={
                isCreate
                    ? t('pages.establishments.editor.newTitle')
                    : row?.locales?.find((locale) => locale.language === CONTENT_BASE_LANGUAGE)
                          ?.name ||
                      row?.title ||
                      t('pages.establishments.editor.fallbackTitle', { id: establishmentId })
            }
            subtitle={row ? [row.cityTitle, row.slug].filter(Boolean).join(' · ') : undefined}
            status={
                row
                    ? {
                          label: enumLabel(t, 'enums.establishmentStatus', row.status),
                          color: statusColor[row.status],
                      }
                    : undefined
            }
            dirty={formDirty || localeDirty || photosDirty}
            tabs={
                isCreate
                    ? [{ value: 'card', label: t('pages.establishments.editor.tabs.card') }]
                    : [
                          {
                              value: 'card',
                              label: t('pages.establishments.editor.tabs.card'),
                              dirty: cardDirty,
                          },
                          {
                              value: 'intake',
                              label: t('pages.establishments.editor.tabs.intake'),
                              dirty: intakeDirty,
                          },
                          {
                              value: 'text',
                              label: t('pages.establishments.editor.tabs.text'),
                              dirty: localeDirty,
                          },
                          {
                              value: 'photos',
                              label: t('pages.establishments.editor.tabs.photos'),
                              dirty: photosDirty,
                          },
                          {
                              value: 'facts',
                              label: t('pages.establishments.editor.tabs.facts'),
                          },
                      ]
            }
            activeTab={tab}
            onTabChange={setTab}
            footer={
                tab === 'facts' ? undefined : (
                    <EditorFooter
                        onCancel={onClose}
                        onSave={
                            tab === 'text' ? saveLocale : tab === 'photos' ? savePhotos : saveCard
                        }
                        saving={saving}
                        left={
                            !isCreate && tab === 'card' ? (
                                <Button color='red' variant='subtle' onClick={() => void remove()}>
                                    {t('pages.establishments.editor.delete')}
                                </Button>
                            ) : undefined
                        }
                    />
                )
            }
        >
            {tab === 'card' && (
                <Stack gap='md'>
                    {!isCreate && row ? (
                        <Section title={t('pages.establishments.editor.ownerBox')} boxed>
                            <Group gap={6}>
                                <Badge
                                    variant='light'
                                    color={
                                        row.ownerConfirm === 'confirmed'
                                            ? 'green'
                                            : row.ownerConfirm === 'link_sent'
                                              ? 'yellow'
                                              : 'gray'
                                    }
                                >
                                    {t(
                                        `pages.establishments.owner.${row.ownerConfirm || 'unconfirmed'}`,
                                    )}
                                </Badge>
                                {row.intakeReview === 'pending' ||
                                row.intakeReview === 'rejected' ? (
                                    <Badge
                                        variant='light'
                                        color={row.intakeReview === 'pending' ? 'orange' : 'red'}
                                    >
                                        {t(`pages.establishments.review.${row.intakeReview}`)}
                                    </Badge>
                                ) : null}
                            </Group>
                            <Text size='xs' c='dimmed'>
                                {t('pages.establishments.editor.intakeVisited')}{' '}
                                {stamp(row.lastVisitedAt, i18n.language)}
                                {' · '}
                                {t('pages.establishments.editor.intakeEdited')}{' '}
                                {stamp(row.lastEditedAt, i18n.language)}
                                {' · '}
                                {t('pages.establishments.editor.intakeLinkSent')}{' '}
                                {stamp(row.linkSentAt, i18n.language)}
                            </Text>
                            <Group gap='xs'>
                                <Button
                                    size='compact-sm'
                                    variant='light'
                                    leftSection={<IconCopy size={14} />}
                                    onClick={() => void copyIntakeLink()}
                                >
                                    {t('pages.establishments.editor.intakeLink')}
                                </Button>
                                {row.whatsapp ? (
                                    <Button
                                        size='compact-sm'
                                        variant='light'
                                        color='green'
                                        leftSection={<IconBrandWhatsapp size={14} />}
                                        onClick={() => void sendIntake('whatsapp')}
                                    >
                                        {t('pages.establishments.editor.sendWhatsapp')}
                                    </Button>
                                ) : null}
                                {row.line ? (
                                    <Button
                                        size='compact-sm'
                                        variant='light'
                                        color='teal'
                                        leftSection={<IconBrandLine size={14} />}
                                        onClick={() => void sendIntake('line')}
                                    >
                                        {t('pages.establishments.editor.sendLine')}
                                    </Button>
                                ) : null}
                                {row.instagram ? (
                                    <Button
                                        size='compact-sm'
                                        variant='light'
                                        color='grape'
                                        leftSection={<IconBrandInstagram size={14} />}
                                        onClick={() => void sendIntake('instagram')}
                                    >
                                        {t('pages.establishments.editor.sendInstagram')}
                                    </Button>
                                ) : null}
                            </Group>
                            {hasChannel ? null : (
                                <Text size='xs' c='dimmed'>
                                    {t('pages.establishments.editor.intakeNoChannel')}
                                </Text>
                            )}
                            {showPack && row.intakePending ? (
                                <Box
                                    p='sm'
                                    style={{
                                        borderRadius: 'var(--mantine-radius-sm)',
                                        border: `1px solid var(--mantine-color-${
                                            row.intakeReview === 'pending' ? 'orange' : 'red'
                                        }-4)`,
                                    }}
                                >
                                    <Text size='xs' fw={700} c='dimmed' tt='uppercase'>
                                        {t('pages.establishments.editor.packTitle')}
                                        {' · '}
                                        {stamp(row.intakePending.submittedAt, i18n.language)}
                                    </Text>
                                    {packLines.length ? (
                                        <Stack gap={2} mt={6}>
                                            {packLines.map((line) => (
                                                <Text key={line.label} size='sm' lineClamp={2}>
                                                    <Text span size='sm' c='dimmed'>
                                                        {line.label}:
                                                    </Text>{' '}
                                                    {line.value}
                                                </Text>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Text size='sm' mt={6}>
                                            {t('pages.establishments.editor.packSame')}
                                        </Text>
                                    )}
                                    {profileHasAnswers(row.intakePending.profile) ? (
                                        <Text size='xs' c='dimmed' mt={6}>
                                            {t('pages.establishments.editor.packChips')}
                                            {row.intakePending.profile.photoCredit
                                                ? ` · ${t('pages.establishments.editor.photoCredit')}: ${row.intakePending.profile.photoCredit}`
                                                : ''}
                                        </Text>
                                    ) : null}
                                    <Group gap='xs' mt='sm'>
                                        <Button
                                            size='xs'
                                            loading={saving}
                                            onClick={() => void reviewIntake('approve')}
                                        >
                                            {t('pages.establishments.editor.intakeApprove')}
                                        </Button>
                                        {row.intakeReview === 'pending' ? (
                                            <Button
                                                size='xs'
                                                variant='default'
                                                loading={saving}
                                                onClick={() => void reviewIntake('reject')}
                                            >
                                                {t('pages.establishments.editor.intakeReject')}
                                            </Button>
                                        ) : null}
                                    </Group>
                                </Box>
                            ) : null}
                        </Section>
                    ) : null}

                    <Section title={t('pages.establishments.editor.identity')} boxed>
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
                            {isCreate ? (
                                <TextInput
                                    label={t('pages.establishments.editor.title')}
                                    required
                                    {...form.getInputProps('title')}
                                />
                            ) : null}
                            <TextInput
                                label={t('pages.establishments.editor.slug')}
                                {...form.getInputProps('slug')}
                            />
                            <CitySearchSelect
                                label={t('pages.establishments.editor.city')}
                                value={form.values.cityId}
                                onChange={(cityId) => {
                                    form.setFieldValue('cityId', cityId);
                                    if (cityId !== form.values.cityId) {
                                        form.setFieldValue('districtId', null);
                                    }
                                }}
                                initialLabel={row?.cityTitle}
                            />
                            <DistrictSelect
                                cityId={form.values.cityId}
                                label={t('pages.establishments.editor.district')}
                                value={form.values.districtId}
                                onChange={(districtId) =>
                                    form.setFieldValue('districtId', districtId)
                                }
                            />
                            <Select
                                label={t('pages.establishments.editor.kind')}
                                data={kindOptions}
                                allowDeselect={false}
                                {...form.getInputProps('kind')}
                            />
                            <Select
                                label={t('pages.establishments.editor.status')}
                                data={statusOptions}
                                allowDeselect={false}
                                {...form.getInputProps('status')}
                            />
                            <TextInput
                                label={t('pages.establishments.editor.neighborhood')}
                                {...form.getInputProps('neighborhood')}
                            />
                        </SimpleGrid>
                    </Section>
                </Stack>
            )}

            {tab === 'intake' && row ? (
                <EstablishmentIntakeTab
                    form={form as unknown as UseFormReturnType<IntakeShape>}
                    country={row.country}
                    specialtyChoices={specialtyChoices}
                    mapHref={mapHref}
                />
            ) : null}

            {tab === 'text' && (
                <Stack gap='md'>
                    <LanguageChips
                        languages={chips}
                        value={lang}
                        onChange={(value) => setLang(value as Languages)}
                    />
                    <Section title={t('pages.establishments.editor.copyTitle')} boxed>
                        <TextInput
                            label={t('pages.establishments.editor.displayName')}
                            value={draft.name}
                            onChange={(event) => patchDraft({ name: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.establishments.editor.pitch')}
                            description={t('pages.establishments.editor.pitchHint')}
                            autosize
                            minRows={3}
                            value={draft.pitch}
                            onChange={(event) => patchDraft({ pitch: event.currentTarget.value })}
                        />
                        <Textarea
                            label={t('pages.establishments.editor.findHint')}
                            description={t('pages.establishments.editor.findHintHelp')}
                            autosize
                            minRows={2}
                            value={draft.findHint}
                            onChange={(event) =>
                                patchDraft({ findHint: event.currentTarget.value })
                            }
                        />
                        {lang === CONTENT_BASE_LANGUAGE ? (
                            <Text size='xs' c='dimmed'>
                                {t('pages.establishments.editor.hoursNoteOnIntake')}
                            </Text>
                        ) : (
                            <Textarea
                                label={t('pages.establishments.editor.hoursNote')}
                                description={t('pages.establishments.editor.hoursNoteHint')}
                                autosize
                                minRows={2}
                                value={draft.hoursNote}
                                onChange={(event) =>
                                    patchDraft({ hoursNote: event.currentTarget.value })
                                }
                            />
                        )}
                        <TagsInput
                            label={t('pages.establishments.editor.tags')}
                            description={t('pages.establishments.editor.tagsHint')}
                            value={draft.tags}
                            maxTags={8}
                            splitChars={[',']}
                            onChange={(tags) =>
                                patchDraft({
                                    tags: tags
                                        .map((tag) => tag.trim().replace(/\s+/g, ' '))
                                        .filter((tag) => tag && tag.length <= 40)
                                        .slice(0, 8),
                                })
                            }
                        />
                        <Switch
                            label={t('pages.establishments.editor.ready')}
                            description={t('pages.establishments.editor.readyHint')}
                            checked={draft.status === EstablishmentLocaleStatusEnum.READY}
                            onChange={(event) =>
                                patchDraft({
                                    status: event.currentTarget.checked
                                        ? EstablishmentLocaleStatusEnum.READY
                                        : EstablishmentLocaleStatusEnum.EMPTY,
                                })
                            }
                        />
                    </Section>
                </Stack>
            )}

            {tab === 'photos' && (
                <PhotoUpload
                    variant='gallery'
                    label={t('pages.establishments.editor.photos')}
                    description={t('pages.establishments.editor.photosHint')}
                    value={photos}
                    coverIndex={cover}
                    imageType={ImageTypeEnum.CITY_ESTABLISHMENT}
                    onChange={(urls) => {
                        setPhotos(urls);
                        setCover((current) => {
                            if (!urls.length) return null;
                            if (current == null || current >= urls.length) return 0;

                            return current;
                        });
                    }}
                    onCoverIndexChange={setCover}
                />
            )}

            {tab === 'facts' && establishmentId && (
                <EstablishmentFactsPanel
                    establishmentId={establishmentId}
                    title={row?.title || String(establishmentId)}
                />
            )}
            {establishmentId && (
                <FieldHistoryPanel target={{ kind: 'establishment', id: establishmentId }} />
            )}
        </EntityEditorShell>
    );
};
